import React, { useEffect, useState } from "react";
import { useFormik } from "formik"; // ✅ Added Formik
import * as Yup from "yup"; // ✅ Added Yup
import Modal from "@mui/material/Modal";
import {
  Box,
  Button,
  Input,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { UploadMedia } from "../utils/mediaUpload";
import { CONTENT_TYPE, TYPE_SUBJECT_LISTING } from "../constants/enums";
import { useCreateContentMutation, useUpdateContentMutation } from "../service/content";
import { showToast } from "../constants/toast";
import CancelIcon from "@mui/icons-material/Cancel";
import { useLazyGetSubjectsAndCategoryQuery } from "../service/auth";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

interface AddFormProps {
  open: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  data?: any;
  edit?: boolean;
}

// ✅ Yup validation schema added
const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  subject: Yup.array()
    .of(Yup.string()) 
    .min(1, "At least one subject is required")
    .required("At least one subject is required"),
  description: Yup.string().required("Description is required"),
  category: Yup.string().required("Category is required"),

});

export const AddFormAndDisscussion = ({
  open,
  handleOpen,
  handleClose,
  data,
  edit
}: AddFormProps) => {
  const [createContent] = useCreateContentMutation();
  const [updateContent] = useUpdateContentMutation();
  const [getSubjects] = useLazyGetSubjectsAndCategoryQuery();
  const [subjects, setSubjects] = useState<any[]>([]);
  const [category, setCategory] = useState<any[]>([]);


  const formik = useFormik({
    initialValues: {
      title: data?.title || "",
      subject: data?.subjects?.map((s: any) => s._id) || [],
      category: data?.category?._id || "",
      description: data?.description || "",
      images: data?.images ? data?.images : [] as string[],
    },
    validationSchema,
    onSubmit: async (values) => {
      handleClose();
      const body = {
        subjectId: values.subject,
        categoryId: values.category,
        title: values.title,
        description: values.description,
        images: values.images,
        contentType: CONTENT_TYPE.FORUM,
      };
      try {
        let res;
        if (edit) {
          res = await updateContent({ id: data._id, body: body }).unwrap();
        } else {

          res = await createContent(body).unwrap();
        }
        if (res?.statusCode === 200) {
          showToast("Forum added successfully");
          formik.resetForm();
        }
      } catch (error: any) {
        console.log(error);
      } finally {
        handleClose();
      }
    },
  });


  const fetchCategory = async () => {
    try {
      const res = await getSubjects({ type: TYPE_SUBJECT_LISTING.CATEGORY }).unwrap();
      if (res?.statusCode == 200) {
        setCategory(res?.data)
      }
    } catch (error: any) {

    }
  }

  const fetchSubjects = async () => {
    try {
      const res = await getSubjects({ type: TYPE_SUBJECT_LISTING.SUBJECT, categoryId: formik.values.category }).unwrap();
      if (res?.statusCode == 200) {
        setSubjects(res?.data)
      }
    } catch (error: any) {

    }
  }
  // ✅ Handle image upload and limit to 4 files
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files).slice(0, 4); // Max 4
    try {
      const uploaded = await Promise.all(
        fileArray.map((file) => UploadMedia(file))
      );
      const uploadedUrls = uploaded.map((res) => res.data?.image); // adjust based on UploadMedia response shape
      formik.setFieldValue("images", [
        ...formik.values.images,
        ...uploadedUrls,
      ]);
    } catch (error) {
      console.error("Image upload failed:", error);
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    const updatedImages = formik.values.images.filter(
      (_: any, idx: any) => idx !== indexToRemove
    );
    formik.setFieldValue("images", updatedImages);
  };


  useEffect(() => {
    fetchCategory();
  }, [])

  useEffect(() => {
    if (formik.values.category) {
      fetchSubjects();
    }
  }, [formik.values.category])
  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div className="form_discuss add_post">
          {/* <div
            className="btn-close"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              cursor: "pointer",
            }}
          >
            <CloseIcon onClick={handleClose} />
          </div> */}
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Add Forum & Discussion
          </Typography>

          {/* ✅ Wrapped form in formik.handleSubmit */}
          <form
            className="form"
            onSubmit={formik.handleSubmit}
            style={{ marginTop: "20px" }}
          >
            <div className="ovr_flow">
              {formik.values.images &&
                formik.values.images.map((img: any, index: number) => (
                  <div className="upload_file">
                    <figure key={index}>
                      <img
                        src={img || `/static/images/emili.png`}
                        alt="emili"
                      />
                      <figcaption
                        onClick={() => handleRemoveImage(index)}
                        style={{ cursor: "pointer" }}
                      >
                        <CancelIcon />
                      </figcaption>
                    </figure>
                  </div>
                ))}
            </div>
            {/* ✅ File input with max 4 images logic */}
            {formik.values.images.length < 4 && (
              <div className="upload_file">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <DriveFolderUploadIcon />
                <p>Upload Images</p>

                {/* ✅ Image upload validation message */}
                {formik.errors.images &&
                  typeof formik.errors.images === "string" && (
                    <Typography color="error" fontSize={12}>
                      {formik.errors.images}
                    </Typography>
                  )}
              </div>
            )}

            {/* ✅ Title input with Formik bindings */}
            <div className="control_group" style={{ marginTop: "15px" }}>
              <Input
                fullWidth
                placeholder="Enter title"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.title && formik.errors.title && (
                <Typography color="error" fontSize={12}>
                  {formik.errors.title as string}
                </Typography>
              )}
            </div>

            <div className="control_group" style={{ marginTop: "15px" }}>
              <Select
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select category
                </MenuItem>
                {category?.map((item) => {
                  return (
                    <MenuItem value={item?._id}>{item?.name || "-"}</MenuItem>
                  )
                })}

              </Select>
              {formik.touched.category && formik.errors.category && (
                <Typography color="error" fontSize={12}>
                  {formik.errors.category as string}
                </Typography>
              )}
            </div>

            <div className="control_group" style={{ marginTop: "15px" }}>
              <Select
                name="subject"
                multiple
                value={formik.values.subject}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                disabled={!formik.values.category}
                displayEmpty
                renderValue={(selected: any) =>
                  subjects
                    .filter((s) => selected.includes(s._id))
                    .map((s) => s.name)
                    .join(", ") || "Select Subject"
                }
              >
                {subjects?.map((item) => (
                  <MenuItem key={item?._id} value={item?._id}>
                    {item?.name || "-"}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.subject && formik.errors.subject && (
                <Typography color="error" fontSize={12}>
                  {formik.errors.subject as string}
                </Typography>
              )}
            </div>


            <div className="control_group" style={{ marginTop: "15px" }}>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="Description"
                minRows={5}
                multiline
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.description && formik.errors.description && (
                <Typography color="error" fontSize={12}>
                  {formik.errors.description as string}
                </Typography>
              )}
            </div>

            <div
              className="form_btn"
            >
              <button className="btn transparent" onClick={handleClose}>
                Cancel
              </button>
              <button type="submit" className=" btn primary">
                {edit ? "Save" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );
};



